export interface ContentProfileSubject {
  subject: string
  published?: boolean
  locked?: boolean
  grades?: string[]
  grades_locked?: string[] | null
  categories?: string[]
  categories_locked?: string[] | null
}

export interface LockableGoal {
  primary_subject: string
  grades: string[]
  domains: string[]
}

export interface GoalFilterSelection {
  subject?: string
  subjectDomain?: string
  grades: string[]
}

function findSubjectProfile(subjects: ContentProfileSubject[], subject: string | undefined) {
  return subjects.find((entry) => entry.subject === subject)
}

function lockedGradesFor(subjects: ContentProfileSubject[], subject: string | undefined): string[] {
  return findSubjectProfile(subjects, subject)?.grades_locked ?? []
}

function lockedCategoriesFor(subjects: ContentProfileSubject[], subject: string | undefined): string[] {
  return findSubjectProfile(subjects, subject)?.categories_locked ?? []
}

export function isSubjectLocked(subjects: ContentProfileSubject[], subject: string | undefined): boolean {
  return Boolean(findSubjectProfile(subjects, subject)?.locked)
}

export function isSubjectDomainLocked(subjects: ContentProfileSubject[], subject: string | undefined, category: string | undefined): boolean {
  if (!category) {
    return false
  }
  return lockedCategoriesFor(subjects, subject).includes(category)
}

export function isSubjectGradeLocked(subjects: ContentProfileSubject[], subject: string | undefined, grade: string): boolean {
  return lockedGradesFor(subjects, subject).includes(grade)
}

// A goal with no grades or no domains counts as locked, since every() of nothing is true.
export function isGoalLocked(subjects: ContentProfileSubject[], goal: LockableGoal): boolean {
  const lockedGrades = lockedGradesFor(subjects, goal.primary_subject)
  const lockedCategories = lockedCategoriesFor(subjects, goal.primary_subject)
  const isEveryGradeLocked = goal.grades.every((grade) => lockedGrades.includes(grade))
  const isEveryCategoryLocked = goal.domains.every((domain) => lockedCategories.includes(domain))
  return isSubjectLocked(subjects, goal.primary_subject) || isEveryGradeLocked || isEveryCategoryLocked
}

export function filterLockedBrowseGoalsState(subjects: ContentProfileSubject[], selection: GoalFilterSelection): GoalFilterSelection {
  if (isSubjectLocked(subjects, selection.subject)) {
    return { subject: undefined, subjectDomain: undefined, grades: [] }
  }
  const isSelectedCategoryLocked = isSubjectDomainLocked(subjects, selection.subject, selection.subjectDomain)
  const unlockedGrades = selection.grades.filter((grade) => !isSubjectGradeLocked(subjects, selection.subject, grade))
  return {
    subject: selection.subject,
    subjectDomain: isSelectedCategoryLocked ? undefined : selection.subjectDomain,
    grades: unlockedGrades
  }
}
